import React, { useState, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';

import styles from './RoleBasedSkillsForm.module.css';
import { useGetRoleBasedSkillsHook } from '../../../../utils/utilityHooks';
import FormSelectField from '../../../UI/FormUI/FormSelectField/FormSelectField';
import FormField from '../../../UI/FormUI/FormField/FormField';
import Button from '../../../UI/Buttons/Button/Button';
import { Close, Add } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';

function RoleBasedSkillsForm({ onSubmit, excludeSkills = [] }) {
    const allOptions = useGetRoleBasedSkillsHook();
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [skillInput, setSkillInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const user = useSelector(state => state.user.user);
    const [isLoading, setIsLoading] = useState(true);

    // Filter out already selected roles
    const options = useMemo(() => {
        if (allOptions && allOptions.length > 0) {
            setIsLoading(false);
            return allOptions.filter(option => 
                !excludeSkills.some(excluded => 
                    // Check by ID or value to handle both formats
                    excluded === option.id || excluded === option.value
                )
            );
        }
        return [];
    }, [allOptions, excludeSkills]);

    const formik = useFormik({
        initialValues: {
            selectedRole: '',
            skillInput: ''
        },
        validationSchema: Yup.object({
            selectedRole: Yup.string().required(t('validation.required')),
        }),
        onSubmit: (values) => {
            onSubmit({
                role: values.selectedRole,
                skills: selectedSkills
            }, options);
        }
    });

    // Set default selected role based on user's working_title if available
    useEffect(() => {
        if (user?.working_title && options.length > 0) {
            const matchingOption = options.find(option => 
                option.value.toLowerCase() === user.working_title.toLowerCase() || 
                option.label.toLowerCase() === user.working_title.toLowerCase()
            );
            
            if (matchingOption) {
                formik.setFieldValue('selectedRole', matchingOption.value);
            }
        }
    }, [user, options]);

    const handleRoleChange = (value) => {
        formik.setFieldValue('selectedRole', value);
        setSelectedSkills([]);
        setSkillInput('');
        setSuggestions([]);
    };

    const handleSkillInputChange = (e) => {
        const input = e.target.value;
        setSkillInput(input);
        formik.setFieldValue('skillInput', input);

        // Get selected role's skills
        const selectedRoleObj = options.find(opt => opt.value === formik.values.selectedRole);
        if (selectedRoleObj && input) {
            const filteredSuggestions = selectedRoleObj.skills.filter(skill => 
                skill.toLowerCase().includes(input.toLowerCase()) && 
                !selectedSkills.includes(skill)
            );
            setSuggestions(filteredSuggestions);
        } else {
            setSuggestions([]);
        }
    };

    const handleSkillSelect = (skill) => {
        if (!selectedSkills.includes(skill)) {
            setSelectedSkills([...selectedSkills, skill]);
        }
        setSkillInput('');
        formik.setFieldValue('skillInput', '');
        setSuggestions([]);
    };

    const handleAddSkill = () => {
        if (skillInput.trim()) {
            // Check for exact match in suggestions first
            const exactMatch = suggestions.find(
                suggestion => suggestion.toLowerCase() === skillInput.trim().toLowerCase()
            );
            
            if (exactMatch) {
                // If there's an exact match in suggestions, use that
                handleSkillSelect(exactMatch);
            } else {
                // If no exact match in suggestions, use the input value as is
                handleSkillSelect(skillInput.trim());
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && skillInput.trim()) {
            e.preventDefault();
            handleAddSkill();
        }
    };

    const removeSkill = (skillToRemove) => {
        setSelectedSkills(selectedSkills.filter(skill => skill !== skillToRemove));
    };

    if (isLoading) {
        return (
            <div className={styles.loaderContainer}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <form onSubmit={formik.handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
                <FormSelectField
                    name="selectedRole"
                    value={formik.values.selectedRole}
                    onChange={handleRoleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.selectedRole && formik.errors.selectedRole}
                    options={options}
                    placeholder={t('general.UI.select_job_title')}
                    type="default"
                    hasBorder
                />
            </div>

            {formik.values.selectedRole && (
                <div className={styles.formGroup}>
                    <div className={styles.skillInputContainer}>
                        <div className={styles.inputWithButton}>
                            <FormField
                                type="text"
                                name="skillInput"
                                value={skillInput}
                                onChange={handleSkillInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder={t('general.UI.type_to_add_skills')}
                                hasBorder
                            />
                            <button 
                                type="button"
                                className={styles.addButton}
                                onClick={handleAddSkill}
                                disabled={!skillInput.trim()}
                            >
                                <Add />
                            </button>
                        </div>
                        {suggestions.length > 0 && (
                            <div className={styles.suggestions}>
                                {suggestions.map((suggestion, index) => (
                                    <div
                                        key={index}
                                        className={styles.suggestionItem}
                                        onClick={() => handleSkillSelect(suggestion)}
                                    >
                                        {suggestion}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.selectedSkills}>
                        {selectedSkills.map((skill, index) => (
                            <div key={index} className={styles.skillTag}>
                                {skill}
                                <Close
                                    className={styles.removeSkill}
                                    onClick={() => removeSkill(skill)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Button 
                type="submit" 
                className={styles.submitButton}
                disabled={!formik.values.selectedRole || selectedSkills.length === 0}
            >
                {t('general.UI.add')}
            </Button>
        </form>
    );
}

export default RoleBasedSkillsForm; 